import React from 'react';
import { Link } from 'react-router-dom';
import Header, { BreadcrumbItem } from './Header';
import { useAuth } from '../context/AuthContext';
import { usePageTracking } from '../hooks/usePageTracking';
import styles from '../styles/programLanding.module.css';

export interface ProgramData {
  slug: string;
  name: string;
  fullName: string;
  description: string;
  overview: string;
  officialWebsite: string;
  officialAssessment?: string;
  newcomerPage?: string;
  meetingFinder: string;
  meetingFinderText: string;
  recoveryCommonsTools?: string[];
  recoveryCommonsRoute?: string;
}

interface ProgramLandingTemplateProps {
  program: ProgramData;
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const ProgramLandingTemplate: React.FC<ProgramLandingTemplateProps> = ({ 
  program, 
  user, 
  onLogout, 
  onLogin 
}) => {
  const { forceClearAuth } = useAuth();
  
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: program.name, path: `/${program.slug}`, active: true }
  ];

  // Track page visit
  usePageTracking(`${program.name} Program Landing`);

  return (
    <div className={styles.programLanding}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <h1 className={styles.heroTitle}>{program.fullName}</h1>
          <p className={styles.heroSubtitle}>{program.description}</p>
          <div className={styles.heroActions}>
            <a 
              href="#official-resources" 
              className={styles.startHereButton}
            >
              Start here
            </a>
          </div>
        </div>

        {/* Overview Section */}
        <section className={styles.overviewSection}>
          <div className={styles.container}>
            <p className={styles.overviewText}>{program.overview}</p>
          </div>
        </section>

        {/* Official Resources Section */}
        <section id="official-resources" className={styles.resourcesSection}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Official Resources</h2>
            <div className={styles.resourcesGrid}>
              <div className={styles.resourceCard}>
                <h3>Official Website</h3>
                <a 
                  href={program.officialWebsite} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.resourceLink}
                >
                  Visit {program.name} website
                </a>
              </div>
              
              {program.officialAssessment && (
                <div className={styles.resourceCard}>
                  <h3>Official Self-Assessment</h3>
                  <a 
                    href={program.officialAssessment} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.resourceLink}
                  >
                    Take {program.name} assessment
                  </a>
                </div>
              )}
              
              {program.newcomerPage && (
                <div className={styles.resourceCard}>
                  <h3>Newcomer Information</h3>
                  <a 
                    href={program.newcomerPage} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.resourceLink}
                  >
                    Newcomer guide
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Meetings Section */}
        <section className={styles.meetingsSection}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Meetings</h2>
            <div className={styles.meetingsContent}>
              <div className={styles.meetingFinder}>
                <h3>Find a Meeting</h3>
                <a 
                  href={program.meetingFinder} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.meetingLink}
                >
                  {program.meetingFinderText}
                </a>
              </div>
              <div className={styles.meetingNote}>
                <p>What to expect at your first meeting: {program.name} meetings are open to anyone who wants to stop their addictive behavior. You don't need to speak, and you can just listen if you prefer.</p>
              </div>
            </div>
          </div>
        </section>

        {/* On Recovery Commons Section */}
        {program.recoveryCommonsTools && program.recoveryCommonsTools.length > 0 && (
          <section className={styles.recoveryCommonsSection}>
            <div className={styles.container}>
              <h2 className={styles.sectionTitle}>On Recovery Commons</h2>
              <div className={styles.toolsGrid}>
                {program.recoveryCommonsTools.map((tool, index) => (
                  <div key={index} className={styles.toolCard}>
                    <h3>{tool}</h3>
                    {program.recoveryCommonsRoute && (
                      <Link to={program.recoveryCommonsRoute} className={styles.toolLink}>
                        Get started
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Encouragement Section */}
        <section className={styles.encouragementSection}>
          <div className={styles.container}>
            <div className={styles.encouragementContent}>
              <p className={styles.encouragementText}>
                Only you can decide if {program.name} is right for you. You're welcome here either way.
              </p>
            </div>
          </div>
        </section>

        {/* Back to Programs */}
        <section className={styles.backToProgramsSection}>
          <div className={styles.container}>
            <Link to="/discover" className={styles.backToProgramsLink}>
              ← Back to all recovery programs
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProgramLandingTemplate;
